import { getRpc } from '../utils/initEditorSdk';
import { Watermark } from '../utils/watermark';
import { RPC_CONTEXT } from '@cloudstudio/editor-sdk';
import { useEffect, useRef, useState } from 'preact/hooks';
import httpClient from '../utils/httpClient';
import { setDisableExtensions } from '../utils/vsbridge';
import { setPublicConfig } from '../utils/config';
import { initializeAegis } from '../routes/home/gaLog';
import urlUtils from '../utils/urlUtils';
import intl from 'react-intl-universal';
import { MessagePlugin } from 'tdesign-react';
import 'tdesign-react/es/style/index.css';
import 'tdesign-react/es/message/style/index.css';

import {
  buildUrl,
  getAppParams,
  getChannelSource,
  getRegionUrl,
  getReportParams,
  getSearchParams,
  onCheckHaiAuthorization,
  onResHandler,
  retry,
} from '../components/app.util';
import { haiAuthorizationUrl } from '../constants/app';
import {
  onSignToken,
  onStartHaiInstance,
  onStatusCheck,
  onSynHaiEnter,
  queryHaiBySpaceKey,
  queryHaiStatus,
  runWorkspace,
  queryHaiAccountFreeTime,
  queryWorkspaceToken,
  queryWorkspaceBySpaceKey,
} from '../services/app';
import {
  HaiInfo,
  HaiStatus,
  onPostRegionUrlParams,
  onSignTokenRes,
  onSynHaiEnterRes,
  HaiLoadingInfo,
} from '../components/app.type';
import useRefresh from './useParamsRefresh';
import { HaiSpecTypeEnum, HaiStatusEnum } from '../components/app.constants';
import { usePrevious } from './index';
// 安全增强
async function doSecret({ content }) {
  const w = new Watermark({
    container: window.parent.document.body,
    content,
  });
  w.observe(() => {
    window.parent.location.reload();
  });
  try {
    const rpc = await getRpc();
    const cmdService = rpc.getProxy(RPC_CONTEXT.CommandService);
    await Promise.all([
      // 禁用下载
      cmdService.executeCommand('cloudstudio.disableDownload'),
      // 复制加密
      cmdService.executeCommand('cloudstudio.setCryptoKey', content),
    ]);
  } catch (err) {
    console.error(err);
  }
}
export const noComputePowerCode = [17122, 17123, 17103, 17108];
function useInitialApp(): {
  config: any;
  postRegionUrlParams: any;
  errorMsg: string;
  haiErrorTitle: string;
  handleHaiStart: () => Promise<void>;
  haiLoadingInfo: HaiLoadingInfo;
} {
  const codeMap = new Map<number, string>([
    [17001, intl.get('无效的工作空间')],
    [17110, intl.get('工作空间正在创建中，请等待创建完成')],
    [17112, intl.get('工作空间已销毁')],
    [17013, intl.get('体验额度已用完，请等待下月发放')],
    [17116, intl.get('账号已欠费，请充值后重试')],
    [17108, intl.get('账号已欠费，请充值后重试')],
    ...noComputePowerCode.map((code) => [code, String(code)] as [number, string]),
  ]);
  const spaceKeyRef = useRef(urlUtils.getSpaceKey() || '');
  const [config, setConfig] = useState({});
  const [postRegionUrlParams, setPostRegionUrlParams] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [haiStatus, setHaiStatus] = useState();
  const [haiErrorTitle, setHaiErrorTitle] = useState(intl.get('开机失败'));
  const [statusChangeReason, setStatusChangeReason] = useState({});
  const haiAccInfo = useRef();
  const preStatus = usePrevious(haiStatus);
  const customerReport = {
    open: false,
    onError: (error) => {
      return Promise.reject(error);
    },
  };

  const { promise: onCheckHaiStatus, cancel: cancelHaiStatus } = retry(
    async () => {
      const res = await onResHandler<HaiStatus>(await queryHaiStatus(spaceKeyRef.current), customerReport);
      setHaiStatus(res?.status);
      setStatusChangeReason(res?.statusChangeReason);
      if (res?.status === HaiStatusEnum.running) {
        return true;
      }
      throw new Error(`hai status error or timeout`);
    },
    60 * 10,
    1000,
  );
  useRefresh();

  useEffect(() => {
    // 状态由starting变为stopped，停止retry并抛出错误
    if (haiStatus === HaiStatusEnum.stopped && preStatus === HaiStatusEnum.starting) {
      const errorMsg = codeMap.get(statusChangeReason?.code);
      setErrorMsg(errorMsg);
      cancelHaiStatus();
    }
  }, [haiStatus]);

  const getPublicConfig = async () => {
    const res = await httpClient({
      path: `/public/config`,
      method: 'GET',
    });
    if (!res || res.code !== 0 || !res.data) {
      console.warn('[ws-loader] /public/config failed', {
        code: res?.code,
        message: res?.message,
        traceId: res?.traceId,
      });
      // 失败也要 resolve 全局 Deferred，避免 vsbridge.disableExtensions / app.util.onResHandler 永久 await
      setPublicConfig({});
      return {};
    }
    setPublicConfig(res.data);
    return res.data;
  };

  const userInfoPromise = () =>
    httpClient({
      path: '/user/info',
      method: 'GET',
    });

  const onNormalInit = () => {
    setDisableExtensions();
    async function fetch() {
      const configData = await getPublicConfig();
      initializeAegis(configData);
      const [res2, res3, res4] = await Promise.all([
        httpClient({
          path: `/workspace/${spaceKeyRef?.current}/share/partners/authorization`,
          method: 'GET',
        }),
        userInfoPromise(),
        onStatusCheck(spaceKeyRef.current),
      ]);
      // 检查是否是分享模式的参与者
      if (res2.code === 0) {
        configData.isPartner = true;
      }
      configData.userInfo = res3;
      configData.spec = res4.data?.spec;
      // 判断工作空间是否重复启停状态
      const isStoppedSpace = res4.data?.status === 'STOPPED' && res4.data?.lastStopIntervalTime <= 5;
      if (isStoppedSpace) {
        setErrorMsg(intl.get('空间停止中'));
      }
      setConfig(configData);
      if (configData.secretEnabled && urlUtils.isEmbeddedLoader()) {
        const username = res3.data?.authenticationUserInfo.nickName || urlUtils.getCookie('cloudstudio-name');
        const id = res3.data?.id || 'unknown';
        const content = `${id}(${username})`;
        doSecret({ content });
      }
    }
    fetch();
  };

  const onHandlerUnAuth = async () => {
    const { code, msg } = getAppParams();
    if (code && msg) {
      let error = '';
      // 504 单独处理
      if (code === '504') {
        error = intl.get(
          '可能为磁盘空间用尽或内存用尽导致，请联系客服或前往高性能工作空间列表点击查看机器来源手动扩容或重启',
        );
        setHaiErrorTitle(intl.get('打开工作空间失败'));
      } else {
        error = `${code} ${msg}`;
        setHaiErrorTitle(intl.get('开机失败'));
      }

      setErrorMsg(error);
      throw new Error(error);
    }
  };

  const initHaiAuthAndReportConfig = async () => {
    const configData = await getPublicConfig();
    const userInfo = await userInfoPromise();
    await onHandlerUnAuth();
    await queryHaiAccInfo();
    configData.userInfo = userInfo;
    setConfig(configData);
  };

  // 查询hai账号信息，用来判断账户类型，判断是否需要授权
  const queryHaiAccInfo = async () => {
    const params = getReportParams();
    let haiInfo;
    try {
      haiInfo = await onResHandler<HaiInfo>(await queryHaiBySpaceKey(spaceKeyRef.current), params);
    } catch (error) {
      const errorMsg = codeMap.get(error?.code) || intl.get('未知错误');
      setErrorMsg(errorMsg);
      throw new Error(errorMsg);
    }
    haiAccInfo.current = haiInfo;
  };

  const handleCheckHaiAuthorization = async () => {
    if (haiAccInfo?.current?.type === HaiSpecTypeEnum.basic) return;
    const res = await onCheckHaiAuthorization();
    if (!res?.granted) {
      location.href = haiAuthorizationUrl;
      return;
    }
  };

  const handleSynHaiEnter = async () => {
    const { region, instanceId } = getAppParams();
    const params = getReportParams();
    try {
      const syncRes = await onSynHaiEnter({ instanceId, region });
      const { spaceKey } = await onResHandler<onSynHaiEnterRes>(syncRes, params);
      if (spaceKey) {
        spaceKeyRef.current = spaceKey;
      }
    } catch (error) {
      throw new Error(error?.message);
    }
  };

  // hai channel, start之前已经通过instanceId获取对应spaceKey了
  const startHai = async () => {
    const params = getReportParams();
    await onResHandler(await onStartHaiInstance({ spaceKey: spaceKeyRef.current }), params);
    const haiStatusParams = getReportParams();
    try {
      await onCheckHaiStatus();
    } catch (error) {
      onResHandler(error, haiStatusParams);
      setErrorMsg('hai status error or timeout');
      throw new Error('hai status error or timeout');
    }
  };

  const startWs = async () => {
    await runWorkspace(spaceKeyRef.current);
  };

  const handleSignToken = async () => {
    const params = getReportParams();
    const { token } = await onResHandler<onSignTokenRes>(await onSignToken(spaceKeyRef.current), params);
    const { region } = getAppParams();
    let synRegion = region;
    // token illegal 302 case, need query hai info by spaceKey
    if (!synRegion && haiAccInfo?.current) {
      synRegion = haiAccInfo?.current?.region;
    }
    const regionUrl = `${getRegionUrl({ spaceKey: spaceKeyRef.current, region: synRegion })}`;
    setPostRegionUrlParams({ token, url: regionUrl });
  };

  const handleStartAndGetToken = async () => {
    try {
      await Promise.all([startHai(), onStatusCheck(spaceKeyRef.current)]);
      await startWs();
      await handleSignToken();
    } catch (error) {
      const errorMsg = codeMap.get(error?.code) || intl.get('未知错误');
      setErrorMsg(errorMsg);
    }
  };

  const onHaiOriginCsInit = async () => {
    try {
      await initHaiAuthAndReportConfig();
      await handleCheckHaiAuthorization();
      await handleStartAndGetToken();
    } catch (error) {
      setErrorMsg(error?.message);
    }
  };

  const handleHaiStart = async () => {
    await initHaiAuthAndReportConfig();
    await handleStartAndGetToken();
  };

  const onHaiOriginHaiInit = async () => {
    try {
      await handleCheckHaiAuthorization();
      await handleSynHaiEnter();
      const res = await queryHaiStatus(spaceKeyRef.current);
      setHaiStatus(res?.data?.status);
      if (res?.data?.status === HaiStatusEnum.running) {
        handleHaiStart();
      }
    } catch (error) {
      setErrorMsg(error?.message);
    }
  };

  const onWorkspaceInit = async () => {
    urlUtils.rmWorkspaceToken();
    try {
      const { token } = await onResHandler<Pick<onSignTokenRes, 'token'>>(
        await queryWorkspaceToken(spaceKeyRef.current),
      );
      if (token) urlUtils.setWorkspaceToken(token);
      const mode = getSearchParams().get('mode');
      const workspace = await onResHandler<any>(await queryWorkspaceBySpaceKey(spaceKeyRef.current));
      setPostRegionUrlParams({
        token,
        url: buildUrl(new URL(workspace?.connections?.webIDE), { mode: mode || 'edit' }),
      });
    } catch (err) {
      MessagePlugin.error(err?.msg || '未知错误');
    }
  };

  useEffect(() => {
    const { isNormal, isHaiOriginCs, isHaiOriginHai, isWorkspaceRd } = getChannelSource();
    if (isNormal) {
      onNormalInit();
    }
    if (isHaiOriginCs) {
      onHaiOriginCsInit();
    }
    if (isHaiOriginHai) {
      onHaiOriginHaiInit();
    }
    if (isWorkspaceRd) {
      onWorkspaceInit();
    }

    return () => {
      cancelHaiStatus();
      urlUtils.rmWorkspaceToken();
    };
  }, []);
  const haiLoadingInfo: HaiLoadingInfo = {
    haiStatus,
    spaceKey: spaceKeyRef.current,
  };
  return {
    config,
    postRegionUrlParams,
    errorMsg,
    haiErrorTitle,
    handleHaiStart,
    haiLoadingInfo,
  };
}

export default useInitialApp;
